import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './index.css';
import logo from '../../assests/logo.png';
import homebg1 from '../../assests/homebg1.png';
import homebg2 from '../../assests/homebg2.png';
import homebg3 from '../../assests/homebg3.png';
import homebg4 from '../../assests/homebg4.png';
import homebg5 from '../../assests/homebg5.png';
import { FaWhatsapp, FaInstagram, FaLinkedinIn, FaBars, FaTimes, FaChevronLeft, FaChevronRight } from "react-icons/fa";

const Home = () => {
  const [activeNav, setActiveNav] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const sliderRef = useRef(null);
  const autoPlayRef = useRef(null);

  const backgroundImages = [homebg1, homebg2, homebg3, homebg4, homebg5];

  // Preload images
  useEffect(() => {
    const preloadImages = async () => {
      const imagePromises = backgroundImages.map((src) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.src = src;
          img.onload = resolve;
          img.onerror = resolve;
        });
      });

      await Promise.all(imagePromises);
      setImagesLoaded(true);
    };

    preloadImages();
  }, []);

  // Smooth slide change
  const goToSlide = useCallback((index) => {
    if (isAnimating) return;

    setIsAnimating(true);
    setCurrentSlide(index);

    setTimeout(() => {
      setIsAnimating(false);
    }, 800);
  }, [isAnimating]);

  // Next/Previous handlers
  const nextSlide = useCallback(() => {
    const next = (currentSlide + 1) % backgroundImages.length;
    goToSlide(next);
  }, [currentSlide, backgroundImages.length, goToSlide]);

  const prevSlide = useCallback(() => {
    const prev = currentSlide === 0 ? backgroundImages.length - 1 : currentSlide - 1;
    goToSlide(prev);
  }, [currentSlide, backgroundImages.length, goToSlide]);

  // Auto-play
  useEffect(() => {
    if (!imagesLoaded) return;

    autoPlayRef.current = setInterval(() => {
      nextSlide();
    }, 3000);

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [imagesLoaded, nextSlide]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (isMobileMenuOpen) return;

      if (e.key === 'ArrowLeft') prevSlide();
      if (e.key === 'ArrowRight') nextSlide();
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [prevSlide, nextSlide, isMobileMenuOpen]);

  // Mobile menu handlers
  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  // Prevent body scroll when menu open
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <div className="home-container">
      {/* Header */}
      <header className="header">
        <Link to="/" className="logo-container">
          <img src={logo} alt="Logo" className="logo-image" />
        </Link>

        <nav className="navigation desktop-nav">
          <Link to="/about" className={`nav-item ${activeNav === 'about' ? 'active' : ''}`}
            onMouseEnter={() => setActiveNav('about')}
            onMouseLeave={() => setActiveNav('')}>
            ABOUT
          </Link>
          <Link to="/projects" className={`nav-item ${activeNav === 'projects' ? 'active' : ''}`}
            onMouseEnter={() => setActiveNav('projects')}
            onMouseLeave={() => setActiveNav('')}>
            PROJECTS
          </Link>
          <Link to="/scope" className={`nav-item ${activeNav === 'scope' ? 'active' : ''}`}
            onMouseEnter={() => setActiveNav('scope')}
            onMouseLeave={() => setActiveNav('')}>
            SCOPE
          </Link>
          <Link to="/contact" className={`nav-item ${activeNav === 'contact' ? 'active' : ''}`}
            onMouseEnter={() => setActiveNav('contact')}
            onMouseLeave={() => setActiveNav('')}>
            CONTACT
          </Link>
        </nav>

        <button className="hamburger-icon" onClick={toggleMobileMenu}>
          {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
        </button>
      </header>

      {/* Mobile Menu */}
      <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`} onClick={closeMobileMenu} />

      <nav className={`mobile-nav ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-nav-header">
          <img src={logo} alt="Logo" className="mobile-nav-logo" />
          <button className="mobile-close-btn" onClick={closeMobileMenu}>
            <FaTimes />
          </button>
        </div>

        <div className="mobile-nav-links">
          <Link to="/about" className="mobile-nav-item" onClick={closeMobileMenu}>ABOUT</Link>
          <Link to="/projects" className="mobile-nav-item" onClick={closeMobileMenu}>PROJECTS</Link>
          <Link to="/scope" className="mobile-nav-item" onClick={closeMobileMenu}>SCOPE</Link>
          <Link to="/contact" className="mobile-nav-item" onClick={closeMobileMenu}>CONTACT</Link>
        </div>

        <div className="mobile-nav-social">
          <a href="https://wa.me/919884072874" target="_blank" rel="noopener noreferrer" className="mobile-social-icon whatsapp">
            <FaWhatsapp />
          </a>
          <a href="https://instagram.com/yourusername" target="_blank" rel="noopener noreferrer" className="mobile-social-icon instagram">
            <FaInstagram />
          </a>
          <a href="https://www.linkedin.com/company/lavanya-shankar-architects/" target="_blank" rel="noopener noreferrer" className="mobile-social-icon linkedin">
            <FaLinkedinIn />
          </a>
        </div>
      </nav>

      {/* Hero Slider */}
      <section className="hero-section">
        {!imagesLoaded && (
          <div className="hero-loading">
            <div className="loading-spinner"></div>
          </div>
        )}

        <div
          className="hero-slider-track"
          ref={sliderRef}
          style={{
            transform: `translate3d(-${currentSlide * 100}vw, 0, 0)`
          }}
        >
          {backgroundImages.map((image, index) => (
            <div
              key={index}
              className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
            >
              <img
                src={image}
                alt={`Slide ${index + 1}`}
                className="hero-slide-image"
                loading={index === 0 ? "eager" : "lazy"}
              />
            </div>
          ))}
        </div>

        <div className="hero-overlay"></div>

        {/* Navigation Arrows */}
        <button
          className="carousel-arrow prev"
          onClick={prevSlide}
          disabled={isAnimating}
          aria-label="Previous slide"
        >
          <FaChevronLeft />
        </button>

        <button
          className="carousel-arrow next"
          onClick={nextSlide}
          disabled={isAnimating}
          aria-label="Next slide"
        >
          <FaChevronRight />
        </button>

        {/* Indicators */}
        <div className="carousel-indicators">
          {backgroundImages.map((_, index) => (
            <button
              key={index}
              className={`indicator-dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              disabled={isAnimating}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Social Icons */}
      <aside className="social-icons">
        <a href="https://wa.me/919884072874" target="_blank" rel="noopener noreferrer" className="icon whatsapp" >
          <FaWhatsapp />
        </a>
        <a href="https://www.instagram.com/lavanyaandshankar_architects?igsh=MW5uY3R5MmI3cmF6NA==" target="_blank" rel="noopener noreferrer" className="icon instagram">
          <FaInstagram />
        </a>
        <a href="https://www.linkedin.com/company/lavanya-shankar-architects/" target="_blank" rel="noopener noreferrer" className="icon linkedin">
          <FaLinkedinIn />
        </a>
      </aside>
    </div>
  );
};

export default Home;